import { createAction, createReducer } from '@reduxjs/toolkit';

import { Geometry } from 'ol/geom';

import { GuideManager, Loadable, BFMCManager } from 'models';
import { makeThunkFromAPICall } from 'state';
import { FEATURE, isFeatureEnabled, LOCALSTORAGE } from 'config';
import { dateReceiver } from 'utils';

import MOCK_LAYER_REVIEW from 'data/mockLayerReviewData';

export type GuideState = {
    BFMCs: Loadable<BFMCManager.BFMC[]>;
    BFMCGroups: Loadable<BFMCManager.BFMCGroup[]>;
    selectedBFMC: BFMCManager.BFMC | null;
    currentStep: GuideManager.Step | null;
    selectedPlanType: BFMCManager.PlanType;
    selectedPlan: BFMCManager.Plan | null;
    layerReview: GuideManager.LayerReview;
    commentModal: GuideManager.LayerReviewModal;
    addPlanResponse: Loadable<BFMCManager.API.ActionResponse>;
    updatePlanResponse: Loadable<BFMCManager.API.ActionResponse>;
    sendDataToServiceNowResponse: Loadable<BFMCManager.API.ActionResponse>;
    layerReviewControl: GuideManager.LayerReviewControl;
    updateStatusResponse: Loadable<BFMCManager.API.ActionResponse>;
    resetBFMCResponse: Loadable<BFMCManager.API.ActionResponse>;
    getBFMCGeometryResponse: Loadable<Geometry | null>;
};

const initialState: GuideState = {
    BFMCs: {
        status: 'idle',
    },
    BFMCGroups: {
        status: 'idle',
    },
    currentStep: null,
    selectedPlanType: !isFeatureEnabled(FEATURE.FAFT_UI)
        ? 'bfrmp'
        : (JSON.parse(
              localStorage.getItem(LOCALSTORAGE.SELECTEDPLANTYPE) ??
                  (null as any),
              dateReceiver,
          ) ?? 'bfrmp'),
    selectedPlan: null,
    selectedBFMC: JSON.parse(
        localStorage.getItem(LOCALSTORAGE.SELECTEDBFMC) ?? (null as any),
        dateReceiver,
    ),
    commentModal: {
        stepId: '',
        title: '',
        isOpen: false,
    },
    sendDataToServiceNowResponse: {
        status: 'idle',
    },
    addPlanResponse: {
        status: 'idle',
    },
    updatePlanResponse: {
        status: 'idle',
    },
    layerReview: MOCK_LAYER_REVIEW,
    layerReviewControl: {
        enabled: false,
        type: '',
    },
    updateStatusResponse: {
        status: 'idle',
    },
    resetBFMCResponse: {
        status: 'idle',
    },
    getBFMCGeometryResponse: {
        status: 'idle',
    },
};

export const setSelectedBFMC = createAction<BFMCManager.BFMC | null>(
    'guide/selected/bfmc',
);

export const setCurrentStep = createAction<GuideManager.Step | null>(
    'guide/selected/step',
);

export const setSelectedPlanType = createAction<BFMCManager.PlanType>(
    'guide/selected/planType',
);

export const [getBFMCs, setBFMCs] = makeThunkFromAPICall(
    BFMCManager.getBFMCs,
    'guide/bfmcs',
);

export const [getBFMCGroups, setBFMCGroups] = makeThunkFromAPICall(
    BFMCManager.getBFMCGroups,
    'guide/bfmcs-groups',
);

export const [getBFMCGeometry, setBFMCGeometry] = makeThunkFromAPICall(
    BFMCManager.getBFMCGeometry,
    'guide/bfmc-geometry',
);

export const [sendUpdateStatus, setUpdateStatusResponse] = makeThunkFromAPICall(
    BFMCManager.updateStatus,
    'guide/bfmcs/update',
);

export const [sendDataToServiceNow, setSendDataToServiceNowResponse] =
    makeThunkFromAPICall(
        BFMCManager.sendDataToServiceNow,
        'guide/bfmc/send-data/servicenow',
    );

export const [sendAddPlan, setAddPlanResponse] = makeThunkFromAPICall(
    BFMCManager.addPlan,
    'guide/bfmcs/plans/add',
);

export const [updatePlanDetails, setUpdatePlanDetails] = makeThunkFromAPICall(
    BFMCManager.updatePlanDetails,
    'guide/bfmcs/plans/update',
);

export const setCommentModal = createAction<GuideManager.LayerReviewModal>(
    'guide/layerReview/modal',
);

export const setLayerReview =
    createAction<GuideManager.LayerReview>('guide/layerReview');

export const setLayerReviewControl =
    createAction<GuideManager.LayerReviewControl>('guide/layerReview/controls');

export const guideReducer = createReducer(initialState, (builder) => {
    builder.addCase(setBFMCs, (state, action) => {
        return {
            ...state,
            BFMCs: { ...state.BFMCs, ...action.payload.loadable },
        };
    });

    builder.addCase(setBFMCGroups, (state, action) => {
        return {
            ...state,
            BFMCGroups: { ...state.BFMCGroups, ...action.payload.loadable },
        };
    });

    builder.addCase(setBFMCGeometry, (state, action) => {
        return {
            ...state,
            getBFMCGeometryResponse: {
                ...state.getBFMCGeometryResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setSelectedBFMC, (state, action) => {
        if (action.payload == null) {
            localStorage.removeItem(LOCALSTORAGE.SELECTEDBFMC);
        } else {
            localStorage.setItem(
                LOCALSTORAGE.SELECTEDBFMC,
                JSON.stringify(action.payload),
            );
        }
        const selectedPlan =
            action.payload && action.payload.plans
                ? action.payload.plans.find(
                      (plan) => plan.plan_type === state.selectedPlanType,
                  )
                : null;
        return {
            ...state,
            selectedBFMC: action.payload,
            selectedPlan: selectedPlan || null,
        };
    });

    builder.addCase(setCurrentStep, (state, action) => {
        return { ...state, currentStep: action.payload };
    });

    builder.addCase(setSelectedPlanType, (state, action) => {
        if (action.payload == null) {
            localStorage.removeItem(LOCALSTORAGE.SELECTEDPLANTYPE);
        } else {
            localStorage.setItem(
                LOCALSTORAGE.SELECTEDPLANTYPE,
                JSON.stringify(action.payload),
            );
        }
        const selectedPlan = state.selectedBFMC?.plans
            ? state.selectedBFMC.plans.find(
                  (plan) => plan.plan_type === action.payload,
              )
            : null;
        return {
            ...state,
            selectedPlanType: action.payload,
            selectedPlan: selectedPlan || null,
        };
    });

    builder.addCase(setCommentModal, (state, action) => {
        return {
            ...state,
            commentModal: { ...action.payload },
        };
    });

    builder.addCase(setSendDataToServiceNowResponse, (state, action) => {
        return {
            ...state,
            sendDataToServiceNowResponse: {
                ...state.sendDataToServiceNowResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setAddPlanResponse, (state, action) => {
        return {
            ...state,
            addPlanResponse: {
                ...state.addPlanResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setUpdatePlanDetails, (state, action) => {
        return {
            ...state,
            updatePlanResponse: {
                ...state.updatePlanResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setLayerReview, (state, action) => {
        return {
            ...state,
            layerReview: { ...action.payload },
        };
    });

    builder.addCase(setLayerReviewControl, (state, action) => {
        return {
            ...state,
            layerReviewControl: { ...action.payload },
        };
    });

    builder.addCase(setUpdateStatusResponse, (state, action) => {
        return {
            ...state,
            updateStatusResponse: {
                ...state.updateStatusResponse,
                ...action.payload.loadable,
            },
        };
    });
});

export const GuideActions = {
    getBFMCs,
    getBFMCGroups,
    getBFMCGeometry,
    sendUpdateStatus,
    setCurrentStep,
    setSelectedPlanType,
    setSelectedBFMC,
    setCommentModal,
    sendAddPlan,
    updatePlanDetails,
    sendDataToServiceNow,
    setLayerReview,
    setLayerReviewControl,
};

export default guideReducer;
